<template>
  <div data-testid="operation-details-content">
    <Grid class="operation-details" :columns="gridColumns" :gutter="32">
      <Header
        :custom-operation-data="customOperationData"
        @tooltip-click="handleOpenTooltipModal"
      />
      <OperationResult
        :custom-operation-data="customOperationData"
        @tooltip-click="handleOpenTooltipModal"
      />
      <Progress :custom-operation-data="customOperationData" />
      <OperationData
        :custom-operation-data="customOperationData"
        :toggle-dynamic-modal="toggleDynamicModal"
      />
      <StrikeOptions
        v-if="hasStrikeOptions"
        :custom-operation-data="customOperationData"
      />
      <Barriers
        v-if="hasMonitoringBarriers"
        :custom-operation-data="customOperationData"
      />
    </Grid>
    <TooltipModal
      :is-open="isTooltipModalOpen"
      :title="tooltipData.title"
      :description="tooltipData.description"
      @close="handleCloseTooltipModal"
    />
    <DynamicContentModal
      :is-open="isDynamicContentModalOpen"
      :title="dynamicContentModal.title"
      :modal-info="dynamicContentModal.description"
      :costs-button="dynamicContentModal.showCostsButton"
      @close="toggleDynamicModal"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

import { Progress } from './components/Progress';
import { OperationData } from './components/OperationData';
import { StrikeOptions } from './components/StrikeOptions';
import { Barriers } from './components/Barriers';
import { OperationResult } from './components/OperationResult';
import { Header } from './components/Header';

import {
  CustomOperation,
  CustomOperationDetailLine
} from '@/modules/trade/services/custom-operations';
import { tooltipsData } from './data';
import { TooltipTypes } from './types';
import { Grid } from '@warrenbrasil/nebraska-web';
import TooltipModal from '@/modules/common/components/TooltipModal/TooltipModal.vue';
import DynamicContentModal from '@/modules/common/components/DynamicContentModal/DynamicContentModal.vue';
import { IDynamicContent } from '@/modules/common/types';

interface Props {
  customOperationData: CustomOperation;
}
const { customOperationData } = defineProps<Props>();

const isTooltipModalOpen = ref(false);
const tooltipData = ref({ title: '', description: '' });

const isDynamicContentModalOpen = ref(false);
const dynamicContentModal = ref({
  title: '',
  description: [] as IDynamicContent[],
  showCostsButton: false
});

const toggleDynamicModal = (row: CustomOperationDetailLine) => {
  if (row) {
    dynamicContentModal.value.title = row.title;
    dynamicContentModal.value.description = row.disclaimer;
    dynamicContentModal.value.showCostsButton = !!row.action;
  }
  isDynamicContentModalOpen.value = !isDynamicContentModalOpen.value;
};

const windowWidth = ref(window.innerWidth);

const updateWidth = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener('resize', updateWidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateWidth);
});

const gridColumns = computed(() => {
  const width = windowWidth.value;
  if (width > 1024) {
    return [1, 1, 1];
  } else if (width > 768) {
    return [1, 1];
  } else {
    return [1];
  }
});

const hasStrikeOptions = computed(() => {
  return (
    customOperationData?.strikeOptions &&
    customOperationData?.strikeOptions?.length > 0
  );
});

const hasMonitoringBarriers = computed(() => {
  return (
    customOperationData?.monitoring &&
    customOperationData?.monitoring?.length > 0
  );
});

const handleOpenTooltipModal = (type: TooltipTypes) => {
  tooltipData.value = tooltipsData[type];
  isTooltipModalOpen.value = true;
};

const handleCloseTooltipModal = () => {
  isTooltipModalOpen.value = false;
};
</script>
<style lang="less" scoped>
.operation-details {
  row-gap: @size-spacing-x500;

  &__content {
    display: flex;
    flex-wrap: wrap;
  }

  &__text-display {
    margin-top: @size-spacing-x500;
  }
}
</style>
